import type { MouseEvent } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sidebar } from "./Sidebar";
import type { NavDrawerState } from "./useNavDrawer";

/**
 * The sidebar as an off-canvas drawer, for widths below `md`.
 *
 * At phone width a fixed 256px rail left about a hundred pixels for the page,
 * so below `md` the rail is hidden and the same sidebar opens over the page
 * from the header's menu button instead. It is the rail's own component, open
 * at full width, so the two can never list different destinations.
 *
 * A modal `Sheet`: focus is held inside while it is open, and Escape or a tap
 * on the backdrop closes it. A change of page closes it too (`useNavDrawer`),
 * and so does following a row to the page already in view, which changes no
 * route but is still the reader choosing where to be.
 */
export function NavDrawer({ nav }: { nav: NavDrawerState }) {
  const closeOnLink = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("a[href]")) nav.setOpen(false);
  };

  return (
    <Sheet open={nav.open} onOpenChange={nav.setOpen}>
      <SheetContent
        id={nav.id}
        side="left"
        // No description: the title names the panel and the rows speak for
        // themselves.
        aria-describedby={undefined}
        // The rail's width and surface, not a sheet's. The close button is
        // moved down to sit level with the logo row's centre.
        className="w-64 border-sidebar-border bg-sidebar p-0 shadow-none sm:max-w-none [&>button:last-child]:top-6"
        // The button lives in the header, outside the sheet, so the sheet does
        // not know where focus came from; hand it back explicitly.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          nav.buttonRef.current?.focus();
        }}
        onClick={closeOnLink}
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <Sidebar inDrawer />
      </SheetContent>
    </Sheet>
  );
}
